import type { ComponentProps } from 'react'
import { useCallback, useMemo } from 'react'
import * as React from 'react'
import { isRoomsInvalid } from 'lib/search/searchUtils'
import TextButton from 'LuxKit/Button/TextButton'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions } from 'selectors/offerPage/offerPageSelectors'
import { hasSelectedRoomsOrDefault } from 'lib/offer/offerPageUtil'
import { useIntl } from 'react-intl'

interface Props extends Omit<ComponentProps<typeof TextButton>, 'kind' | 'children'> {
  rooms: Array<App.RoomOccupants>
  checkIn?: string
  packageOptions: Array<App.OfferPackageOptionView>
  isFlightSelected?: boolean
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  durationSelected?: boolean
  allowViewOptions?: boolean
  room?: App.PackageRoomType
}

function FlashHotelAvailabilityButton(props: Props) {
  const {
    rooms,
    checkIn,
    isFlightSelected,
    onClick,
    durationSelected,
    allowViewOptions,
    room,
    ...rest
  } = props

  const intl = useIntl()
  const offerPageDispatch = useOfferPageDispatch()

  const invalidRoomSelection = !hasSelectedRoomsOrDefault(rooms) || isRoomsInvalid(rooms)
  const canViewOptions = !!checkIn && !invalidRoomSelection
  const ctaLabel = useMemo(() => {
    if (canViewOptions) {
      if (allowViewOptions) {
        if (room) {
          return intl.formatMessage({
            id: 'qqlOD-O8y4W_Kyp8kGyb',
            defaultMessage: 'Select a package',
          })
        }

        return intl.formatMessage({
          id: 'wwzSzE0jlWpwvB3Fwq6Q',
          defaultMessage: 'Select a room',
        })
      }

      if (rooms.length > 1) {
        return intl.formatMessage(
          {
            id: 'eDfJJQuA-doSRuVwf8su',
            defaultMessage: 'View {count, plural, one {# room} other {# rooms}}',
          },
          { count: rooms.length },
        )
      } else {
        return intl.formatMessage({
          id: 'CsM-BWhTHtDxES45Yg_e',
          defaultMessage: 'View rooms',
        })
      }
    }
    return intl.formatMessage({
      id: '_etWrwIdpiw96KvQ_Pzp',
      defaultMessage: 'Check availability',
    })
  }, [allowViewOptions, canViewOptions, room, rooms, intl])

  const onClickAvailability = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e)
    if (checkIn && allowViewOptions) {
      if (room) {
        offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'packageSelection' })
        return
      }

      offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'roomSelection' })
      return
    }

    if (invalidRoomSelection || (!durationSelected && !allowViewOptions)) {
      offerPageDispatch({
        type: OfferPageActions.checkAvailability,
        filter: 'capacity',
        flights: !!isFlightSelected,
      })
      return
    }

    if (!checkIn) {
      offerPageDispatch({
        type: OfferPageActions.checkAvailability,
        filter: 'checkin',
        flights: !!isFlightSelected,
      })

      return
    }

    // Reset previous room selected
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: undefined })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'roomSelection' })
  }, [allowViewOptions, checkIn, durationSelected, invalidRoomSelection, isFlightSelected, offerPageDispatch, onClick, room])

  // Horizontal slider mode hides the "View rooms" path when dates are selected
  if (canViewOptions && !allowViewOptions) {
    return null
  }

  return (
    <TextButton {...rest} kind="primary" onClick={onClickAvailability}>
      {ctaLabel}
    </TextButton>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  room: state.room,
  packageOptions: getDurationPackageOptions(state, appState),
  isFlightSelected: state.flights,
  activeFilter: state.activeFilter,
  durationSelected: state.durationSelected,
}), FlashHotelAvailabilityButton)
